import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { formatError } from '../lib/errors';

// The consent screen of the Supabase OAuth 2.1 server. Supabase sends the
// person here (Site URL + the Authorization Path set under Authentication >
// OAuth Server) with an authorization_id when another app, today the Reality
// Tracker MCP server used from a Claude app, asks to act as them. The admin
// app only hosts the page because the Site URL points at it; the decision and
// the tokens are Supabase's. Nothing here touches booking data.
//
// RequireAuth wraps this route, so by the time it renders the person is
// signed in with the account the other app will act as.

type Details = {
  authorization_id: string;
  client: { client_id?: string; client_name?: string; client_uri?: string; logo_uri?: string } & Record<string, unknown>;
  user: { email: string };
  scope: string;
};

type OAuthResponse = { data: unknown; error: { message: string } | null };

export function OAuthConsentScreen() {
  const { search } = useLocation();
  const { session } = useAuth();
  const authorizationId = new URLSearchParams(search).get('authorization_id') ?? '';
  const [details, setDetails] = useState<Details | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<'approved' | 'denied' | null>(null);

  useEffect(() => {
    if (!authorizationId || !session) return;
    let alive = true;
    (supabase.auth.oauth.getAuthorizationDetails(authorizationId) as Promise<OAuthResponse>)
      .then(({ data, error: err }) => {
        if (!alive) return;
        if (err) {
          setError(err.message);
          return;
        }
        // Already consented earlier: Supabase answers with the redirect straight away.
        if (data && typeof data === 'object' && 'redirect_to' in data) {
          window.location.assign(String((data as { redirect_to: string }).redirect_to));
          return;
        }
        setDetails(data as Details);
      })
      .catch((e: unknown) => alive && setError(formatError(e)));
    return () => {
      alive = false;
    };
  }, [authorizationId, session]);

  const decide = async (approve: boolean) => {
    setBusy(true);
    setError(null);
    try {
      const call = approve ? supabase.auth.oauth.approveAuthorization : supabase.auth.oauth.denyAuthorization;
      const { data, error: err } = (await call.call(supabase.auth.oauth, authorizationId)) as OAuthResponse;
      if (err) throw new Error(err.message);
      setDone(approve ? 'approved' : 'denied');
      const to = data && typeof data === 'object' && 'redirect_to' in data ? String((data as { redirect_to: string }).redirect_to) : '';
      if (to) window.location.assign(to);
    } catch (e) {
      setError(formatError(e));
      setBusy(false);
    }
  };

  const clientName = details?.client.client_name || details?.client.client_id || 'An application';

  return (
    <div className="login-shell">
      <div className="login-card">
        <h1>Toegang geven</h1>
        {!authorizationId ? (
          <p>Deze pagina hoort bij een inlogverzoek van een andere app en is nu direct geopend. Er valt hier niets te doen.</p>
        ) : error ? (
          <p className="error">{error}</p>
        ) : done ? (
          <p>{done === 'approved' ? 'Toegang gegeven. Je kunt dit venster sluiten.' : 'Geweigerd. Je kunt dit venster sluiten.'}</p>
        ) : !details ? (
          <p>Even geduld…</p>
        ) : (
          <>
            <p>
              <strong>{clientName}</strong> wil namens <strong>{details.user.email}</strong> werken in de Reality Tracker: het bord en de
              kennisbank lezen en, als je ontwikkelaar bent, werkitems en taken aanmaken.
            </p>
            {details.client.client_uri ? (
              <p className="muted" style={{ fontSize: '0.85rem' }}>
                {String(details.client.client_uri)}
              </p>
            ) : null}
            <p className="muted" style={{ fontSize: '0.85rem' }}>
              Het krijgt geen toegang tot boekingen of klantgegevens. Intrekken kan later via de Supabase-beheerder.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button type="button" className="btn-primary" disabled={busy} onClick={() => decide(true)}>
                Toestaan
              </button>
              <button type="button" className="btn-secondary" disabled={busy} onClick={() => decide(false)}>
                Weigeren
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
